// The editor canvas stage (Flowmark's): a gray well with the page in a
// device-sized frame. The page lays out at the device's real width (desktop:
// the room there is, clamped to 1024-1440; tablet 820; phone 390) and the
// whole frame is scaled down to fit, so breakpoints and wrapping are the real
// ones. The frame always fills the stage height and the page scrolls inside
// it, never the stage. Switching devices animates only the frame's outer box;
// the page re-lays out at once and keeps its scroll, because the frame (an
// IframePreview) stays mounted.
import { useLayoutEffect, useRef, useState } from 'react';
import IframePreview from '../preview/IframePreview';
import { DEVICE_FRAME, type CanvasMode, type EditorDevice } from './types';

/** Where and how big the page frame is for a device in the room the stage has. */
export interface StageGeometry {
  /** The size the page lays out at inside the frame (CSS px). */
  frameW: number;
  frameH: number;
  /** How much the frame is scaled down to fit (1 = actual size). */
  scale: number;
  /** The frame's size on screen (frame size times scale). */
  boxW: number;
  boxH: number;
}

/** The frame is never shorter than this (layout px), however short the stage. */
export const MIN_FRAME_H = 480;

/** The frame for `device` in a stage whose content box is `avail` (px). An
 *  unmeasured stage (0 x 0, e.g. before the first layout) gets actual size.
 *  Desktop lays out at the room there is, clamped to 1024-1440, and scales down
 *  below 1024; tablet and phone keep their width and scale down when the stage
 *  is narrower. The height fills the stage (stage height / scale, at least
 *  480). Pure. */
export function stageGeometry(device: EditorDevice, avail: { w: number; h: number }): StageGeometry {
  const w = avail.w > 0 ? avail.w : 0;
  const h = avail.h > 0 ? avail.h : 0;
  const frameW = device === 'desktop'
    ? Math.min(DEVICE_FRAME.desktopMax, Math.max(DEVICE_FRAME.desktopMin, Math.floor(w)))
    : DEVICE_FRAME[device];
  const scale = w > 0 ? Math.min(1, w / frameW) : 1;
  const frameH = Math.max(MIN_FRAME_H, h > 0 ? h / scale : 0);
  return { frameW, frameH, scale, boxW: frameW * scale, boxH: frameH * scale };
}

const DEVICE_LABEL: Record<EditorDevice, string> = { desktop: 'Desktop', tablet: 'Tablet', phone: 'Phone' };

// The frame's look on the well. Desktop and tablet are a card (8 / 14px
// corners, a hairline and a soft shadow); the phone gets a 6px bezel drawn as
// a shadow, so the page keeps its full 390px, and below a 640px window only a
// 1px ring with 18px corners. Dark mode swaps the black hairlines for light
// ones and the bezel for the app's surface color (NL-AOS --surface).
const CARD = 'shadow-[0_0_0_1px_rgb(0_0_0/0.07),0_1px_3px_rgb(0_0_0/0.04),0_8px_24px_rgb(0_0_0/0.04)] '
  + 'dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08),0_8px_24px_rgb(0_0_0/0.4)]';
const FRAME_LOOK: Record<EditorDevice, string> = {
  desktop: `rounded-[8px] ${CARD}`,
  tablet: `rounded-[14px] ${CARD}`,
  phone: 'rounded-[28px] shadow-[0_0_0_1px_rgb(0_0_0/0.1),0_0_0_6px_#fff,0_0_0_7px_rgb(0_0_0/0.08),0_12px_32px_rgb(0_0_0/0.08)] '
    + 'dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1),0_0_0_6px_var(--surface),0_0_0_7px_rgb(255_255_255/0.08),0_12px_32px_rgb(0_0_0/0.5)] '
    + '[@media(max-width:640px)]:rounded-[18px] [@media(max-width:640px)]:shadow-[0_0_0_1px_rgb(0_0_0/0.08)] '
    + 'dark:[@media(max-width:640px)]:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]',
};

export default function CanvasStage({ device, mode, onBackgroundClick, children, className }: {
  device: EditorDevice;
  mode: CanvasMode;
  /** A click on the gray well itself (not the page) calls this: the editor
   *  clears the selection. */
  onBackgroundClick?: () => void;
  /** The page (PageView), rendered inside the frame. */
  children: React.ReactNode;
  /** Sizing from the parent, e.g. "min-h-0 flex-1". */
  className?: string;
}) {
  const wellRef = useRef<HTMLDivElement>(null);
  // The well's content box: the room the frame has.
  const [avail, setAvail] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = wellRef.current;
    if (!el) return;
    const measure = () => {
      const cs = el.ownerDocument.defaultView?.getComputedStyle(el);
      const px = (v: string | undefined) => parseFloat(v ?? '') || 0;
      const w = Math.max(0, el.clientWidth - px(cs?.paddingLeft) - px(cs?.paddingRight));
      const h = Math.max(0, el.clientHeight - px(cs?.paddingTop) - px(cs?.paddingBottom));
      setAvail((p) => (p.w === w && p.h === h ? p : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const g = stageGeometry(device, avail);
  return (
    <div ref={wellRef} data-canvas-bg data-device={device} data-mode={mode}
      className={`relative flex items-start justify-center overflow-hidden p-6 [@media(max-width:1023px)]:p-4 [@media(max-width:640px)]:px-2 [@media(max-width:640px)]:py-3 ${className ?? ''}`}
      // clip, not hidden: nothing (a scrollIntoView inside the page) can scroll
      // the well itself. Browsers without clip keep the class's hidden.
      style={{ overflow: 'clip' }}
      onClick={onBackgroundClick ? (e) => { if (e.target === e.currentTarget) onBackgroundClick(); } : undefined}>
      <div data-canvas-frame
        className={`relative shrink-0 overflow-hidden bg-white transition-[width,height] duration-[220ms] ease-[cubic-bezier(.2,0,0,1)] motion-reduce:transition-none ${FRAME_LOOK[device]}`}
        style={{ width: g.boxW, height: g.boxH, overflow: 'clip' }}>
        <div data-canvas-page
          style={{ width: g.frameW, height: g.frameH, transform: g.scale < 1 ? `scale(${g.scale})` : undefined, transformOrigin: '0 0' }}>
          <IframePreview className="h-full w-full" title={`${DEVICE_LABEL[device]} preview`} editorMode={mode}>{children}</IframePreview>
        </div>
      </div>
    </div>
  );
}
